import { t } from "@lingui/macro";
import {
  addMonths,
  endOfMonth,
  format,
  isMatch,
  parse,
  startOfMonth,
  subMonths,
} from "date-fns";
import { useSetAtom, Provider as AtomProvider } from "jotai";
import { useCallback, useEffect, useState } from "react";
import React from "react";
import { Platform } from "react-native";

import { Input } from "@whoosh/design-system/src/components/controls/Input";
import {
  CalendarIcon,
  CloseIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { Menu } from "@whoosh/design-system/src/components/overlay/Menu";
import {
  Box,
  Pressable,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import {
  convertDateObjToServerStringFormat,
  isDateStringValid,
} from "@whoosh/utilities/src/dates";

import { Button } from "../../../Button/Button";
import { calendarSettingsAtom } from "../Calendar/atoms";
import {
  generateDateInfo,
  getParsedDateObj,
  possibleDateFormats,
  tryParseDate,
} from "../Calendar/utils/calendarDateUtils";
import { DatePickerPopup } from "./components/DatePickerPopup";
// import { DatePickerPopup } from "./components/DatePickerPopup";
import { DatePickerInputProps } from "./components/types";
import { getIsValidDatePrefix } from "./hooks/getIsValidDatePrefix";

export const DatePickerInput = (props: DatePickerInputProps) => {
  return <AtomProvider>{<DatePickerInputInner {...props} />}</AtomProvider>;
};

export const DatePickerInputInner = ({
  selectedDate,
  fillEmptyDays,
  onChange,
  onBlur,
  canClear,
  name,
  isDisplayMode,
  placeholder,
  displayValueFormat = "MMM do, yyyy",
  returnValueFormat = "MMM do, yyyy",
  accessibilityLabel,
  "data-testid": dataTestId,
  disableManualEntry,
  firstAvailableDate,
  lastAvailableDate,
  unavailableDates,
  clubWeekStartsOn,
  size = "regular",
  openPosition = "bottom-start",
  testID,
}: DatePickerInputProps) => {
  const resolvedTestId = testID ?? dataTestId;
  const setCalendarSettings = useSetAtom(calendarSettingsAtom);
  const [internalSelectedDate, setInternalSelectedDate] = useState<
    string | undefined
  >(selectedDate ? selectedDate : "");
  const [hasError, setHasError] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean | undefined>(
    undefined
  );
  const [userInteraction, setUserInteraction] = useState<boolean>(false);

  useEffect(() => {
    if (isDisplayMode) {
      setIsCalendarOpen(false);
    }
  }, [isDisplayMode]);

  const today = format(new Date(), "yyyy-MM-dd", {
    weekStartsOn: clubWeekStartsOn,
  });
  const [monthDateObj, setMonthDateObj] = useState(
    getParsedDateObj(internalSelectedDate || today, clubWeekStartsOn)
  );

  useEffect(() => {
    if (isDateStringValid(selectedDate)) {
      setInternalSelectedDate(selectedDate);
      setMonthDateObj(
        getParsedDateObj(selectedDate || today, clubWeekStartsOn)
      );
    }
  }, [selectedDate, today, clubWeekStartsOn]);

  const internalDisplayValueFormat =
    displayValueFormat && possibleDateFormats.includes(displayValueFormat)
      ? displayValueFormat
      : "MMM do, yyyy";

  const _returnValueFormat =
    returnValueFormat && possibleDateFormats.includes(returnValueFormat)
      ? returnValueFormat
      : "MMM do, yyyy";

  const formatReturnDate = useCallback(
    (dateString: string) => {
      const parsedDate = tryParseDate(dateString);
      if (parsedDate && parsedDate instanceof Date) {
        return format(parsedDate, _returnValueFormat);
      }
      return dateString;
    },
    [_returnValueFormat]
  );

  const formatDisplayDate = useCallback(
    (dateString: string) => {
      const parsedDate = tryParseDate(dateString);
      if (parsedDate && parsedDate instanceof Date) {
        return format(parsedDate, internalDisplayValueFormat);
      }
      return dateString;
    },
    [internalDisplayValueFormat]
  );

  useEffect(() => {
    if (selectedDate) {
      const formattedDate = formatDisplayDate(selectedDate);
      setInternalSelectedDate(formattedDate);
    } else {
      setInternalSelectedDate(selectedDate);
    }
  }, [formatDisplayDate, selectedDate]);

  const handleValueChange = useCallback(
    (
      val: string | number,
      {
        commitWhenValidDate = false,
      }: {
        commitWhenValidDate?: boolean;
      } = {}
    ) => {
      if (isDisplayMode) return;

      const value = val.toString();

      const isValidPrefix = getIsValidDatePrefix(value);

      if (!isValidPrefix) {
        setHasError(true);
        return;
      }

      setInternalSelectedDate(value);

      if (!value.length) {
        if (onChange) {
          onChange("");
        }
        setHasError(false);
        return;
      }

      const hasExactDateMatch = possibleDateFormats.some((dateFormat) =>
        isMatch(value, dateFormat)
      );
      const parsedDate = hasExactDateMatch ? tryParseDate(value) : value;

      if (parsedDate instanceof Date) {
        setMonthDateObj(parsedDate);

        if (commitWhenValidDate && onChange) {
          onChange(format(parsedDate, _returnValueFormat));
        }
      }
      setHasError(false);
    },
    [_returnValueFormat, isDisplayMode, onChange]
  );

  const showClearIcon =
    canClear && !isDisplayMode && !!internalSelectedDate?.length;

  const isIOS = Platform.OS === "ios";

  const handleBlur = () => {
    if (isDisplayMode) return;

    if (onBlur) onBlur();
    if (!internalSelectedDate) {
      setHasError(false);
      setUserInteraction(false);
      setIsFocused(false);
      return;
    }

    const hasExactDateMatch = possibleDateFormats.some((dateFormat) =>
      isMatch(internalSelectedDate, dateFormat)
    );
    const parsedDate: Date | string = hasExactDateMatch
      ? tryParseDate(internalSelectedDate)
      : internalSelectedDate;

    if (parsedDate && parsedDate instanceof Date) {
      const formattedDate = format(parsedDate, internalDisplayValueFormat);
      setInternalSelectedDate(formattedDate);
      if (onChange) {
        onChange(format(parsedDate, _returnValueFormat));
      }
      setHasError(false);
    } else {
      setHasError(true);
      const fallbackDate = selectedDate ? formatDisplayDate(selectedDate) : "";
      setInternalSelectedDate(fallbackDate);
    }
    setUserInteraction(false);
    setIsFocused(false);
  };

  const calendarStartDateObject = firstAvailableDate
    ? parse(firstAvailableDate, "yyyy-MM-dd", new Date())
    : startOfMonth(subMonths(monthDateObj, 1));

  const calendarStartDate = firstAvailableDate
    ? firstAvailableDate
    : format(calendarStartDateObject, "yyyy-MM-dd", {
        weekStartsOn: clubWeekStartsOn,
      });

  const calendarEndDateObject = lastAvailableDate
    ? parse(lastAvailableDate, "yyyy-MM-dd", new Date())
    : endOfMonth(addMonths(monthDateObj, 1));
  const calendarEndDate = lastAvailableDate
    ? lastAvailableDate
    : format(calendarEndDateObject, "yyyy-MM-dd", {
        weekStartsOn: clubWeekStartsOn,
      });

  const dateInfo = generateDateInfo(
    calendarStartDateObject,
    calendarEndDateObject,
    "Default"
  );
  const convertDateFormat = (dateString: string): string => {
    if (!isMatch(dateString, internalDisplayValueFormat)) {
      return dateString;
    }
    const parsedDate = parse(
      dateString,
      internalDisplayValueFormat,
      new Date()
    );
    return format(parsedDate, "yyyy-MM-dd");
  };
  if (internalSelectedDate) {
    const isoSelectedDate = convertDateFormat(internalSelectedDate);
    dateInfo.set(isoSelectedDate, { selected: true });
  }

  unavailableDates?.forEach((date) => {
    dateInfo.set(date, { status: "Unavailable" });
  });

  const onDateChange = useCallback(
    (date: Date | undefined | string) => {
      if (isDisplayMode) return;

      const newDate =
        date && date instanceof Date
          ? convertDateObjToServerStringFormat(date)
          : date ?? "";
      const formattedDate = newDate ? formatReturnDate(newDate) : newDate;
      handleValueChange(formattedDate, { commitWhenValidDate: true });
      setUserInteraction(true);
      setIsCalendarOpen(false);
    },
    [formatReturnDate, handleValueChange, isDisplayMode]
  );

  useEffect(() => {
    setCalendarSettings({
      startDate: calendarStartDate,
      endDate: calendarEndDate,
      onDatePress: onDateChange,
      fillEmptyDays: fillEmptyDays ?? true,
      dateInfo: dateInfo ?? null,
      singleDatePicker: true,
      clubWeekStartsOn: clubWeekStartsOn,
    });
  }, [
    calendarStartDate,
    calendarEndDate,
    onDateChange,
    fillEmptyDays,
    dateInfo,
    clubWeekStartsOn,
    setCalendarSettings,
  ]);

  const resolvedAccessibilityLabel =
    accessibilityLabel || placeholder || t`Date`;

  return isIOS ? (
    <Input
      name={name ?? ""}
      isDisabled={isDisplayMode}
      placeholder={placeholder || ""}
      onChange={(value: string | number) => {
        handleValueChange(value);
      }}
      value={internalSelectedDate || ""}
      accessibilityLabel={resolvedAccessibilityLabel}
      rightIcon={
        showClearIcon ? (
          <Pressable onPress={() => handleValueChange("")}>
            <CloseIcon color="inherit" />
          </Pressable>
        ) : undefined
      }
      testID={resolvedTestId ?? "datepickerinput-form-field-ios"}
    />
  ) : (
    <Menu
      overrideIsOpen={isDisplayMode ? false : isCalendarOpen}
      onClose={() => {
        setUserInteraction(true);
        setIsCalendarOpen(false);
      }}
      openPosition={openPosition}
      trigger={() => {
        return !disableManualEntry ? (
          <>
            <Input
              size={size}
              testID={resolvedTestId ?? "datepickerinput-form-field"}
              isDisabled={isDisplayMode}
              name={name ?? ""}
              placeholder={placeholder || ""}
              onChange={(val: string | number) => {
                setHasError(false);
                const value = val.toString();
                handleValueChange(value);
              }}
              value={internalSelectedDate ?? ""}
              onFocus={() => {
                setIsFocused(true);
                if (!userInteraction) {
                  setIsCalendarOpen(true);
                } else {
                  setUserInteraction(false);
                }
              }}
              onBlur={handleBlur}
              accessibilityLabel={resolvedAccessibilityLabel}
              rightIcon={
                showClearIcon ? (
                  <Box position="relative" top={-8} right={6}>
                    <Button
                      onPress={() => handleValueChange("")}
                      testID="datepickerinput-clear-field-icon"
                      size="tiny"
                      variant={"secondary"}
                      leftIcon={<CloseIcon />}
                      accessibilityLabel={t`Clear field icon`}
                    />
                  </Box>
                ) : undefined
              }
              subElement={
                !isDisplayMode && !showClearIcon ? (
                  <Pressable
                    onPress={() => setIsCalendarOpen(true)}
                    testID="datepickerinput-open-calendar-icon"
                  >
                    <CalendarIcon />
                  </Pressable>
                ) : undefined
              }
              hasError={hasError}
              overrideIsFocused={isCalendarOpen || isFocused}
            />
            {hasError ? (
              <Text
                variant="mainTiny"
                color="status-error-500"
                fontWeight="600"
                paddingTop={1}
              >
                {t`Please enter a date in a supported date format`}
              </Text>
            ) : null}
          </>
        ) : (
          <Box position="relative">
            <Pressable
              testID={resolvedTestId ?? "datepickerinput-trigger-only"}
              disabled={isDisplayMode}
              onPress={() => {
                setIsCalendarOpen(!isCalendarOpen);
              }}
              borderRadius={"2"}
              borderColor={
                hasError
                  ? "outline-status-error"
                  : isCalendarOpen
                  ? "grayscale-1000"
                  : "grayscale-500"
              }
              borderWidth={1.5}
              backgroundColor={
                isDisplayMode ? "grayscale-500" : "surface-primary"
              }
              padding={4}
            >
              <Text variant={"mainBaseSemiBold"} lineHeight={16} fontSize={16}>
                {internalSelectedDate || placeholder || t`Select Date`}
              </Text>
            </Pressable>
            {!isDisplayMode ? (
              <Box position="absolute" top={10} right={8} zIndex={10}>
                {showClearIcon ? (
                  <Button
                    onPress={() => handleValueChange("")}
                    testID="datepickerinput-clear-field-icon"
                    size="tiny"
                    variant={"secondary"}
                    leftIcon={<CloseIcon />}
                    accessibilityLabel={t`Clear field icon`}
                  />
                ) : (
                  <Pressable
                    onPress={() => setIsCalendarOpen(true)}
                    testID="datepickerinput-open-calendar-icon"
                    padding={2}
                  >
                    <CalendarIcon />
                  </Pressable>
                )}
              </Box>
            ) : null}
          </Box>
        );
      }}
      content={() => (
        <Menu.Content size={"large"} maxHeight={"100%"}>
          <DatePickerPopup
            monthDateObj={monthDateObj}
            startDate={startOfMonth(monthDateObj)}
            endDate={endOfMonth(monthDateObj)}
            canClear={canClear}
            emitClose={() => {
              setIsCalendarOpen(false);
            }}
            onDateChange={(date: Date | undefined) => {
              onDateChange(date);
            }}
            setMonthDateObj={setMonthDateObj}
            clubWeekStartsOn={clubWeekStartsOn}
          />
        </Menu.Content>
      )}
    />
  );
};
